import React from 'react';
import { Check, CircleSlash, MessageSquareText } from 'lucide-react';
import { tx } from './i18n';
import { Pill } from './parts';
import { QC_TONES, qcPairs, qcRemarks, qcResult, shownPairs } from './qcResults';
import './qcTable.css';

// Our first quality check of a customer's samples, a row for each sample: its name and our code (and,
// for the laboratory, the step it is at), each measurement the laboratory compares (ours, and what
// they sent under it), and its result: Go on and Stop while it waits for the customer's answer, or
// the result with who answered. The customer's portal and the laboratory's page of customers'
// submissions both show it. On a phone each row stacks.
const t = (key, values) => tx(`ui.customer_portal.${key}`, values);
const shortDay = (value) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(value));

// A result as the customer reads it ("waiting for your answer"), or the laboratory.
const resultText = (result, audience) => tx(`ui.${audience === 'lab' ? 'lab.customers' : 'customer_portal'}.qc_result.${result}`);

// Who answered: one of theirs, or one of ours for them; as the customer reads it, or the laboratory.
function answeredBy(answer, audience) {
  const values = { name: answer.by || '', date: shortDay(answer.decidedAt) };
  if (answer.byCustomer) return t('qc_answered_by', values);
  return tx(audience === 'lab' ? 'ui.lab.customers.answered_for' : 'ui.customer_portal.qc_answered_for', values);
}

function QcRow({ sample, pairs, columns, choice, onChoose, busy, audience, noteShown }) {
  const result = qcResult(sample.qc);
  const answer = sample.qc.answer;
  const remarks = qcRemarks(sample.qc);
  return <tr>
    <th scope="row"><strong>{sample.sampleName}</strong><code>{sample.code}</code>
      {audience === 'lab' && sample.step && sample.status === 'in_progress' && !sample.qc.waiting && <small>{sample.step}</small>}</th>
    {qcPairs(pairs, columns, sample).map((pair) => <td key={pair.code} className="is-number" data-label={t(`qc_compare.${pair.code}`)}>
      {pair.ours && <b>{pair.ours.value}{pair.ours.unit && <small>{pair.ours.unit}</small>}</b>}
      {pair.theirs !== null && <span>{t(audience === 'lab' ? 'qc_they_sent' : 'qc_you_sent', { value: pair.theirs })}</span>}
    </td>)}
    <td><div className="xo-qct-result">
      {sample.qc.waiting && onChoose ? <div className="xo-qct-choice" role="group" aria-label={t('qc_choice_label', { name: sample.sampleName })}>
        <button type="button" className={`is-go${choice === 'proceed' ? ' is-on' : ''}`} aria-pressed={choice === 'proceed'} disabled={busy}
          onClick={() => onChoose(sample.position, 'proceed')}><Check aria-hidden="true" />{t('qc_go_on')}</button>
        <button type="button" className={`is-stop${choice === 'stop' ? ' is-on' : ''}`} aria-pressed={choice === 'stop'} disabled={busy}
          onClick={() => onChoose(sample.position, 'stop')}><CircleSlash aria-hidden="true" />{t('qc_stop')}</button>
      </div> : <Pill tone={QC_TONES[result]}>{resultText(result, audience)}</Pill>}
      {answer && <small>{answeredBy(answer, audience)}</small>}
      {answer?.note && !noteShown && <q>{answer.note}</q>}
      {remarks.length > 0 && <p>{remarks.join(' · ')}</p>}
    </div></td>
  </tr>;
}

// The rows of the checked samples. `chosen` holds the answers picked so far by place, `onChoose`
// picks one (none: the choices are not offered). A note that came with every answer shows once,
// under the rows, not on each.
export default function QcTable({ samples, compare, columns, chosen = {}, onChoose = null, busy = false, audience = 'customer' }) {
  const checked = samples.filter((sample) => sample.qc !== null);
  const pairs = shownPairs(compare, columns, checked);
  const answers = checked.map((sample) => sample.qc.answer).filter(Boolean);
  const shared = answers.length > 1 && answers[0].note && answers.every((answer) => answer.note === answers[0].note) ? answers[0].note : null;
  return <>
    <div className="xo-qct-wrap"><table className="xo-qct">
      <thead><tr>
        <th scope="col">{t('qc_column_sample')}</th>
        {pairs.map((pair) => <th scope="col" key={pair.code} className="is-number">{t(`qc_compare.${pair.code}`)}</th>)}
        <th scope="col">{t('qc_column_result')}</th>
      </tr></thead>
      <tbody>{checked.map((sample) => <QcRow key={sample.position} sample={sample} pairs={pairs} columns={columns} choice={chosen[sample.position]}
        onChoose={onChoose} busy={busy} audience={audience} noteShown={shared !== null} />)}</tbody>
    </table></div>
    {shared !== null && <p className="xo-qct-note"><MessageSquareText aria-hidden="true" /><span><small>{t('qc_note_given')}</small><q>{shared}</q></span></p>}
  </>;
}
